import React, { createContext, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { favoritesService, safeSubscribe } from '../services/pocketbase.service';
import { useAuth } from './AuthContext';

const FavoritesContext = createContext({
  count: 0,
  loading: false,
  refresh: async () => {},
});

export const FavoritesProvider = ({ children }) => {
  const { user } = useAuth();
  const [count, setCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const unsubRef = useRef(null);
  const userIdRef = useRef(null);

  const refresh = async () => {
    if (!user?.id) return;
    setLoading(true);
    try {
      const res = await favoritesService.getFavoritesCount(user.id);
      if (res.success) {
        setCount(res.count || 0);
      }
    } catch (e) {
      // noop
    } finally {
      setLoading(false);
    }
  };

  // React to auth changes
  useEffect(() => {
    const id = user?.id || null;
    userIdRef.current = id;

    // Unsubscribe previous
    if (unsubRef.current) {
      try { unsubRef.current(); } catch {}
      unsubRef.current = null;
    }

    if (!id) {
      setCount(0);
      return;
    }

    // Initial load
    refresh();

    // Realtime subscribe to user_favs for this user
    const subscribe = async () => {
      try {
        const unsub = await safeSubscribe('user_favs', '*', (e) => {
          const action = e.action;
          const rec = e.record;
          const targetUserId = rec?.user_id?.id || rec?.user_id || null;
          if (targetUserId === userIdRef.current) {
            // Always refresh count from server to avoid drift or duplication
            // Only react to create/delete actions
            if (action === 'create' || action === 'delete') {
              refresh();
            }
          }
        });
        unsubRef.current = unsub;
      } catch (err) {
        // If subscribe fails, silently ignore
      }
    };
    subscribe();
    
    return () => {
      try {
        if (unsubRef.current) {
          unsubRef.current();
          unsubRef.current = null;
        }
      } catch {}
    };
  }, [user?.id]);

  const value = useMemo(() => ({
    count,
    loading,
    refresh,
  }), [count, loading]);

  return (
    <FavoritesContext.Provider value={value}>{children}</FavoritesContext.Provider>
  );
};

export const useFavorites = () => useContext(FavoritesContext);